import { useCallback, useEffect, useState } from "react";
import { AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import GenieImageSelector from "@/components/agent/vision/GenieImageSelector";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { GenieCreationLoader } from "@/components/ui/GenieCreationLoader";
import { useBrandingExamples } from "@/hooks/agent/useBrandingExamples";
import { useUnfinishedSetups } from "@/hooks/agent/useUnfinishedSetups";
import { usePreselectedAndFavoriteVoices } from "@/hooks/voice/usePreselectedVoices";
import { useVoiceFavorites } from "@/hooks/voice/useVoiceFavorites";
import { useVoices } from "@/hooks/voice/useVoices";
import { cn } from "@/lib/utils";
import { PronunciationPair } from "@/types/agent";
import { SetupAnalysisResult } from "@/types/agent/setup";
import { KnowledgeBase as KnowledgeBaseItem } from "@/types/knowledge/knowledge-base";
import { GenieCategories } from "@/types/shared/categories";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { validateStep } from "../utils/stepValidation";
import {
  ConfirmationAdvancedSection,
  ConfirmationBrandingSection,
  ConfirmationConversationSection,
  ConfirmationGoalsSection,
  ConfirmationIdentitySection,
  ConfirmationKnowledgeSection,
  ConfirmationOwnershipSection,
} from "./confirmation";
import { ConfirmationSummarySection } from "./confirmation/ConfirmationSummarySection";
import { CONFIRMATION_STEPS, StepNavigation } from "./confirmation/StepNavigation";

interface ParameterConfirmationProps {
  analysis: SetupAnalysisResult;
  onConfirm: (updatedAnalysis: SetupAnalysisResult) => void;
  onCancel: () => void;
  isCreating: boolean;
}

export const ParameterConfirmation = ({
  analysis,
  onConfirm,
  onCancel,
  isCreating,
}: ParameterConfirmationProps) => {
  const [editedAnalysis, setEditedAnalysis] = useState<SetupAnalysisResult>(analysis);
  const { markAsAbandoned, isMarkingAbandoned } = useUnfinishedSetups();

  // Knowledge base state
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBaseItem[]>(
    analysis.knowledgeBase || [],
  );
  const [pendingDocumentIds, setPendingDocumentIds] = useState<string[]>(
    analysis.pendingDocumentIds || [],
  ); // Track documents still processing
  const [deferredDocumentActions, setDeferredDocumentActions] = useState<any[]>(
    analysis.deferredDocumentActions || [],
  );

  // Pronunciations state
  const [pronunciations, setPronunciations] = useState<PronunciationPair[]>(
    analysis.pronunciations || [],
  );

  // Step-by-step state
  const [viewMode, setViewMode] = useState<"steps" | "all">("steps");
  const [currentStep, setCurrentStep] = useState("branding");
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const { refetch: refetchVoices } = useVoices();
  const { data: favorites = [] } = useVoiceFavorites();
  const { data: exampleGenies = [], isLoading: loadingExamples } = useBrandingExamples();
  const { voices, isLoading: voicesLoading } = usePreselectedAndFavoriteVoices();

  const handleFieldChange = useCallback((field: keyof SetupAnalysisResult, value: any) => {
    setEditedAnalysis((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleConversationSettingsChange = useCallback((field: string, value: number | string) => {
    setEditedAnalysis((prev) => ({
      ...prev,
      conversationSettings: {
        ...prev.conversationSettings,
        [field]: value,
      },
    }));
  }, []);

  const handleSupportEmailsChange = useCallback((emails: string[]) => {
    setEditedAnalysis((prev) => ({
      ...prev,
      supportEmails: emails,
    }));
  }, []);

  // Knowledge base functions
  const handleKnowledgeBaseToggle = useCallback((doc: any, checked: boolean) => {
    if (checked) {
      setSelectedKnowledgeBase((prev) => [
        ...prev,
        { id: doc.el_doc_id, type: doc.type || "text", name: doc.name },
      ]);
    } else {
      setSelectedKnowledgeBase((prev) => prev.filter((item) => item.id !== doc.el_doc_id));
    }
  }, []);

  const handleKnowledgeBaseBulkToggle = useCallback(
    (docs: any[], checked: boolean) => {
      if (checked) {
        const newDocs = docs
          .filter((doc) => !selectedKnowledgeBase.some((item) => item.id === doc.el_doc_id))
          .map((doc) => ({ id: doc.el_doc_id, type: doc.type || "text", name: doc.name }));
        setSelectedKnowledgeBase((prev) => [...prev, ...newDocs]);
        toast.success(`Linked ${newDocs.length} documents from folder`);
      } else {
        const docIds = new Set(docs.map((doc) => doc.el_doc_id));
        setSelectedKnowledgeBase((prev) => prev.filter((item) => !docIds.has(item.id)));
        toast.success(`Unlinked ${docs.length} documents from folder`);
      }
    },
    [selectedKnowledgeBase],
  );

  // Handle genie image selection
  const handleGenieImageSelect = useCallback((imageUrl: string) => {
    setEditedAnalysis((prev) => ({
      ...prev,
      branding: {
        ...prev.branding,
        customAvatarUrl: imageUrl,
      },
    }));
  }, []);

  const handleConfirm = () => {
    // Add knowledge base, pronunciations, and deferred actions to analysis before confirming
    const updatedAnalysis = {
      ...editedAnalysis,
      knowledgeBase: selectedKnowledgeBase,
      pronunciations: pronunciations,
      pendingDocumentIds: pendingDocumentIds, // Include pending documents for async linking
      deferredDocumentActions: deferredDocumentActions, // Include deferred document actions
    };

    // Show informative toast if there are pending documents or deferred actions
    if (pendingDocumentIds.length > 0) {
      toast.info(
        `Creating your genie now. ${pendingDocumentIds.length} document${
          pendingDocumentIds.length > 1 ? "s" : ""
        } will be linked automatically once processing completes.`,
      );
    }

    if (deferredDocumentActions.length > 0) {
      toast.info(
        `${deferredDocumentActions.length} document action${
          deferredDocumentActions.length > 1 ? "s" : ""
        } will be processed after genie creation.`,
      );
    }

    onConfirm(updatedAnalysis);
  };

  const handleDiscard = async () => {
    try {
      // If there's a setupConversationId, mark it as abandoned
      if (editedAnalysis.setupConversationId) {
        await markAsAbandoned(editedAnalysis.setupConversationId);
        toast.success("Setup discarded");
      }
      // Then reset everything and go back to initial screen
      onCancel();
    } catch (error) {
      console.error("Error discarding setup:", error);
      toast.error("Failed to discard setup");
    }
  };

  // Step navigation handlers
  const handleStepChange = (stepId: string) => {
    setCurrentStep(stepId);
    setValidationErrors([]);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNextStep = () => {
    // Validate current step
    const validation = validateStep(currentStep, editedAnalysis);

    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      toast.error("Please fix the errors before continuing", {
        description: validation.errors[0],
      });
      return;
    }

    // Mark step as completed
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }

    // Find next step
    const currentIndex = CONFIRMATION_STEPS.findIndex((s) => s.id === currentStep);
    if (currentIndex < CONFIRMATION_STEPS.length - 1) {
      const nextStep = CONFIRMATION_STEPS[currentIndex + 1];
      handleStepChange(nextStep.id);
    }
  };

  const handlePreviousStep = () => {
    const currentIndex = CONFIRMATION_STEPS.findIndex((s) => s.id === currentStep);
    if (currentIndex > 0) {
      const previousStep = CONFIRMATION_STEPS[currentIndex - 1];
      handleStepChange(previousStep.id);
    }
  };

  // Skip an optional step: advance without validating and without marking it
  // completed, so the stepper still shows it as open to come back to.
  const currentStepMeta = CONFIRMATION_STEPS.find((s) => s.id === currentStep);
  const canSkipStep = viewMode === "steps" && currentStepMeta ? !currentStepMeta.required : false;

  const handleSkipStep = () => {
    const currentIndex = CONFIRMATION_STEPS.findIndex((s) => s.id === currentStep);
    setCompletedSteps(completedSteps.filter((id) => id !== currentStep));
    if (currentIndex < CONFIRMATION_STEPS.length - 1) {
      handleStepChange(CONFIRMATION_STEPS[currentIndex + 1].id);
    }
  };

  // Auto-validate steps as user edits
  useEffect(() => {
    if (viewMode === "steps") {
      const validation = validateStep(currentStep, editedAnalysis);
      if (validation.isValid && !completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
    }
  }, [editedAnalysis, currentStep, viewMode]);

  // Render step content based on view mode
  const renderStepContent = () => {
    if (viewMode === "all") {
      // Existing all-fields layout
      return (
        <div className="space-y-6">
          <ConfirmationBrandingSection
            analysis={editedAnalysis}
            onChange={handleFieldChange}
            exampleGenies={exampleGenies}
            loadingExamples={loadingExamples}
          />
          <ConfirmationIdentitySection analysis={editedAnalysis} onChange={handleFieldChange} />
          <ConfirmationConversationSection
            analysis={editedAnalysis}
            onChange={handleFieldChange}
            voices={voices}
            voicesLoading={voicesLoading}
            onRefetchVoices={refetchVoices}
            favorites={favorites}
          />
          <ConfirmationGoalsSection
            analysis={editedAnalysis}
            onChange={handleFieldChange}
            onSupportEmailsChange={handleSupportEmailsChange}
          />
          <ConfirmationKnowledgeSection
            selectedKnowledgeBase={selectedKnowledgeBase}
            onDocumentToggle={handleKnowledgeBaseToggle}
            onBulkToggle={handleKnowledgeBaseBulkToggle}
            pendingDocumentIds={pendingDocumentIds}
            onPendingDocumentsChange={setPendingDocumentIds}
            deferredActions={deferredDocumentActions}
            onDeferredActionsChange={setDeferredDocumentActions}
          />
          <ConfirmationAdvancedSection
            analysis={editedAnalysis}
            pronunciations={pronunciations}
            onChange={handleFieldChange}
            onConversationSettingsChange={handleConversationSettingsChange}
            onSupportEmailsChange={handleSupportEmailsChange}
            onPronunciationsChange={setPronunciations}
          />
          <ConfirmationOwnershipSection analysis={editedAnalysis} onChange={handleFieldChange} />
        </div>
      );
    }

    // Step-by-step mode
    switch (currentStep) {
      case "branding":
        return (
          <ConfirmationBrandingSection
            analysis={editedAnalysis}
            onChange={handleFieldChange}
            exampleGenies={exampleGenies}
            loadingExamples={loadingExamples}
          />
        );
      case "identity":
        return (
          <ConfirmationIdentitySection analysis={editedAnalysis} onChange={handleFieldChange} />
        );
      case "conversation":
        return (
          <ConfirmationConversationSection
            analysis={editedAnalysis}
            onChange={handleFieldChange}
            voices={voices}
            voicesLoading={voicesLoading}
            onRefetchVoices={refetchVoices}
            favorites={favorites}
          />
        );
      case "insights":
        return (
          <ConfirmationGoalsSection
            analysis={editedAnalysis}
            onChange={handleFieldChange}
            onSupportEmailsChange={handleSupportEmailsChange}
          />
        );
      case "knowledge":
        return (
          <ConfirmationKnowledgeSection
            selectedKnowledgeBase={selectedKnowledgeBase}
            onDocumentToggle={handleKnowledgeBaseToggle}
            onBulkToggle={handleKnowledgeBaseBulkToggle}
            pendingDocumentIds={pendingDocumentIds}
            onPendingDocumentsChange={setPendingDocumentIds}
            deferredActions={deferredDocumentActions}
            onDeferredActionsChange={setDeferredDocumentActions}
            onSkip={viewMode === "steps" ? handleSkipStep : undefined}
          />
        );
      case "advanced":
        return (
          <ConfirmationAdvancedSection
            analysis={editedAnalysis}
            pronunciations={pronunciations}
            onChange={handleFieldChange}
            onConversationSettingsChange={handleConversationSettingsChange}
            onSupportEmailsChange={handleSupportEmailsChange}
            onPronunciationsChange={setPronunciations}
          />
        );
      case "ownership":
        return <ConfirmationOwnershipSection analysis={editedAnalysis} onChange={handleFieldChange} />;
      case "review":
        return (
          <ConfirmationSummarySection
            analysis={editedAnalysis}
            onEditStep={handleStepChange}
            selectedKnowledgeBase={selectedKnowledgeBase}
          />
        );
      default:
        return null;
    }
  };

  const category =
    GenieCategories.find((cat) => cat.id === editedAnalysis.category) ||
    GenieCategories.find((cat) => cat.id === "other")!;

  // Use branding from analysis if available, otherwise fall back to defaults
  const previewBranding = editedAnalysis.branding || {
    ...DEFAULT_BRANDING,
  };

  // Determine brand color and if custom branding exists
  const brand = previewBranding.primaryColor || DEFAULT_BRANDING.primaryColor;
  const hasBranding = Boolean(
    previewBranding.primaryColor !== DEFAULT_BRANDING.primaryColor ||
    previewBranding.secondaryColor ||
    previewBranding.customAvatarUrl ||
    previewBranding.gradientEnabled,
  );

  return (
    <>
      {/* Loading Animation Overlay */}
      {isCreating && <GenieCreationLoader />}

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Genie Setup?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel? All your changes and configurations will be lost.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={onCancel} className="bg-red-600 hover:bg-red-700">
              Yes, Cancel Setup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Discard Confirmation Dialog */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Setup?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to discard this setup? You'll return to the initial screen. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscard}
              disabled={isMarkingAbandoned}
              className="bg-red-600 hover:bg-red-700"
            >
              {isMarkingAbandoned ? "Discarding..." : "Yes, Discard"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="min-h-screen bg-[#F9FAFC]">
        <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1400px]">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="text-center sm:text-left flex-1">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
                  Review Your Genie
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mt-2">
                  Confirm the details and customize before creation
                </p>
              </div>

              {/* View Mode Toggle and Discard Button */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewMode("steps")}
                    className={cn(
                      "text-sm px-4",
                      viewMode === "steps" ? "text-white" : "text-gray-700 hover:text-gray-900",
                    )}
                    style={
                      viewMode === "steps"
                        ? {
                            backgroundColor: "#4E9CFF",
                          }
                        : undefined
                    }
                    onMouseEnter={(e) => {
                      if (viewMode === "steps") {
                        e.currentTarget.style.backgroundColor = "#3b82f6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (viewMode === "steps") {
                        e.currentTarget.style.backgroundColor = "#4E9CFF";
                      }
                    }}
                  >
                    Step-by-Step
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewMode("all")}
                    className={cn(
                      "text-sm px-4",
                      viewMode === "all" ? "text-white" : "text-gray-700 hover:text-gray-900",
                    )}
                    style={
                      viewMode === "all"
                        ? {
                            backgroundColor: "#4E9CFF",
                          }
                        : undefined
                    }
                    onMouseEnter={(e) => {
                      if (viewMode === "all") {
                        e.currentTarget.style.backgroundColor = "#3b82f6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (viewMode === "all") {
                        e.currentTarget.style.backgroundColor = "#4E9CFF";
                      }
                    }}
                  >
                    All Fields
                  </Button>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowDiscardDialog(true)}
                  disabled={isCreating || isMarkingAbandoned}
                  className="ml-auto h-10 border-2 border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold transition-all text-sm"
                >
                  Discard
                </Button>
              </div>
            </div>

            {/* Step Navigation - only in step mode */}
            {viewMode === "steps" && (
              <StepNavigation
                currentStep={currentStep}
                completedSteps={completedSteps}
                onStepChange={handleStepChange}
              />
            )}

            {/* Voice Widget Container for All Fields mode - positioned absolutely to not take space */}
            {viewMode === "all" && (
              <div className="hidden sm:block absolute top-24 right-8 z-40">
                <div id="voice-widget-container" className="flex items-center" />
              </div>
            )}
          </div>

          {/* Template Recommendations - Hidden but still works in background */}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <Alert className="mb-6 bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <ul className="list-disc list-inside space-y-1">
                  {validationErrors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Main Configuration Sections */}
          {renderStepContent()}

          {/* Step Navigation Buttons - only in step mode */}
          {viewMode === "steps" && (
            <div className="flex gap-4 mt-8 pt-6 border-t-2 border-gray-200">
              {currentStep !== "branding" && (
                <Button
                  variant="ghost"
                  onClick={handlePreviousStep}
                  disabled={isCreating}
                  className="px-6 h-12 text-base text-gray-600"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Back
                </Button>
              )}

              {canSkipStep && currentStep !== "review" && (
                <Button
                  variant="ghost"
                  onClick={handleSkipStep}
                  disabled={isCreating}
                  className="px-6 h-12 self-center text-base text-gray-500 hover:text-gray-700"
                >
                  Skip this step
                </Button>
              )}

              {currentStep !== "review" ? (
                <>
                  <Button
                    onClick={handleNextStep}
                    disabled={isCreating}
                    className="flex-1 h-14 text-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                    style={{ backgroundColor: "#4E9CFF" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#3b82f6")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#4E9CFF")}
                  >
                    Continue to{" "}
                    {
                      CONFIRMATION_STEPS[
                        CONFIRMATION_STEPS.findIndex((s) => s.id === currentStep) + 1
                      ]?.shortLabel
                    }
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleConfirm}
                  disabled={isCreating}
                  className="flex-1 h-16 text-xl font-bold text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
                  style={{
                    background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
                  }}
                >
                  {isCreating ? "Creating Genie..." : "✓ Create My Genie"}
                </Button>
              )}
            </div>
          )}

          {/* All Fields Mode - Action Buttons */}
          {viewMode === "all" && (
            <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t-2 border-gray-200">
              <Button
                onClick={handleConfirm}
                disabled={isCreating}
                className="w-full sm:flex-1 h-12 text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                style={{
                  background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
                }}
              >
                {isCreating ? "Creating Genie..." : "Create Genie"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCancelDialog(true)}
                disabled={isCreating}
                className="w-full sm:flex-1 h-12 border-2 border-gray-300 hover:bg-gray-50 font-semibold transition-all hover:scale-105"
              >
                Cancel
              </Button>
            </div>
          )}

          {/* Mobile sticky action bar: visible only in step mode */}
          {viewMode === "steps" && (
            <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white/95 backdrop-blur border-t border-gray-200 p-3 z-30">
              <div className="flex items-center gap-3">
                {currentStep !== "branding" && (
                  <Button
                    variant="outline"
                    onClick={handlePreviousStep}
                    disabled={isCreating}
                    className="flex-1 h-12"
                  >
                    Back
                  </Button>
                )}
                {canSkipStep && currentStep !== "review" && (
                  <Button
                    variant="ghost"
                    onClick={handleSkipStep}
                    disabled={isCreating}
                    className="flex-1 h-12 text-gray-500"
                  >
                    Skip
                  </Button>
                )}
                {currentStep !== "review" ? (
                  <>
                    <Button
                      onClick={handleNextStep}
                      disabled={isCreating}
                      className="flex-[2] h-12 text-white"
                      style={{ backgroundColor: "#4E9CFF" }}
                    >
                      Continue
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleConfirm}
                    disabled={isCreating}
                    className="flex-[2] h-12 text-white"
                    style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
                  >
                    {isCreating ? "Creating..." : "Create Genie"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
